import React from "react";
import { Segmented } from "antd";
import { useTranslation } from "react-i18next";
import { LETTER_LANGUAGES, LetterLanguage } from "../blocks";
import { Device } from "../Builder/LetterSheet";

import styles from "./index.module.scss";

interface ViewSwitchesProps {
  device: Device;
  onDevice: (device: Device) => void;
  language: LetterLanguage;
  onLanguage: (language: LetterLanguage) => void;
}

/**
 * How a letter is being looked at: on which device, and in which language.
 *
 * The pair travels together wherever a letter is shown — the builder's canvas,
 * the send step's preview and the drawer a campaign opens in — and had been
 * written out in all three, which is three places for one control to drift.
 *
 * They sit as one group, 8 apart, because they answer one question between
 * them: what the reader of this letter is looking at.
 *
 * TODO(design): the letter has one set of blocks, so the language switch only
 * moves the control. A developer needs a version of the content per language
 * behind it. See DEVELOPER_TASKS.md.
 */
export default function ViewSwitches({
  device,
  onDevice,
  language,
  onLanguage,
}: ViewSwitchesProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.switches}>
      <Segmented<Device>
        value={device}
        onChange={onDevice}
        options={[
          { value: "desktop", label: t("pages.campaigns.builder.desktop") },
          { value: "mobile", label: t("pages.campaigns.builder.mobile") },
        ]}
      />

      <Segmented<LetterLanguage>
        value={language}
        onChange={onLanguage}
        options={LETTER_LANGUAGES.map((value) => ({
          value,
          label: value.toUpperCase(),
        }))}
      />
    </div>
  );
}
